import axios from 'axios';
import { logout } from '../api/auth/authSlice';
import { fetchSubscriptionUsageRequest } from '../api/subscription/subscriptionSlice';
import { showSnackbar } from '../api/snackbarSlice';
import snackbarMessagesByLang from '../locales/snackbar_messages.json';
import { LANGUAGES } from '../constants/appConstants';
import appConfig from '../config/appConfig';
import { readRequiresOrgSelection, savePostAuthRedirect } from './authSession';

const MUTATION_METHODS = ['post', 'put', 'patch'];

/** Lazy store access breaks axios ↔ store ↔ codeListsSlice circular import at module init. */
const getStore = () => require('../store').default;

const refreshSubscriptionUsageIfNeeded = () => {
  const store = getStore();
  const subscriptionId = store.getState().subscription?.subscription?.stripe_subscription_id;
  if (subscriptionId) {
    store.dispatch(fetchSubscriptionUsageRequest({ subscriptionId }));
  }
};

const shouldRefreshSubscriptionUsage = (response) => {
  const method = response.config?.method?.toLowerCase();
  if (!MUTATION_METHODS.includes(method) || response.status < 200 || response.status >= 300) {
    return false;
  }

  const url = response.config?.url ?? '';
  if (url.includes('/subscriptions/') && url.includes('/usage')) {
    return false;
  }

  return true;
};

const getSnackbarMessages = () => {
  const state = getStore().getState();
  const language = state?.language?.language ?? LANGUAGES.EN;
  return snackbarMessagesByLang[language] ?? snackbarMessagesByLang[LANGUAGES.EN];
};

const apiClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Version': '20260507',
    'X-Marmin-App': 'E_INVOICE',
  },
});

const authApiClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/auth`,
  withCredentials: true,
  headers: {
    'X-Marmin-App': 'E_INVOICE',
  },
});

const billingClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/billing`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Version': '20260507',
    'X-Marmin-App': 'E_INVOICE',
  },
});

const analyticsClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/analytics`,
  withCredentials: true,
  // Fiber PeekMulti expects repeated keys (a=1&a=2), not axios default a[]=1&a[]=2.
  paramsSerializer: { indexes: null },
  headers: {
    'Content-Type': 'application/json',
    'X-API-Version': '20260507',
    'X-Marmin-App': 'E_INVOICE',
  },
});

// Reconciliation backend, reached through Kong like every other service:
// mauth validates the Bearer token and injects X-User-Id / X-Org-Id upstream,
// so the client carries only the JWT (no manual identity headers).
const reconClient = axios.create({
  baseURL: `${appConfig.apiBaseUrl}/reconciliation/v1`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-API-Version': '20260507',
    'X-Marmin-App': 'E_INVOICE',
  },
});

export async function endSession() {
  try {
    await authApiClient.post('/logout', {}, { skipSessionRedirect: true, skipErrorSnackbar: true });
  } catch {
    // Clearing the cookie is best-effort when the network call fails.
  }

  getStore().dispatch(logout());
}

let sessionExpiryStarted = false;

function expireAuthenticatedSession(message) {
  const auth = getStore().getState().auth;
  if (!auth?.isAuthenticated || sessionExpiryStarted) {
    return;
  }

  sessionExpiryStarted = true;
  getStore().dispatch(
    showSnackbar({
      message,
      severity: 'error',
    }),
  );
  endSession().finally(() => {
    window.location.href = '/login';
  });
}

apiClient.interceptors.response.use(
  (response) => {
    const { common } = getSnackbarMessages();
    if (response.status === 202) {
      getStore().dispatch(
        showSnackbar({
          message: response.data?.message || common.request_accepted_success,
          severity: 'success',
        }),
      );
    }
    if (shouldRefreshSubscriptionUsage(response)) {
      refreshSubscriptionUsageIfNeeded();
    }
    return response;
  },
  (error) => {
    if (error?.config?.skipErrorSnackbar) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status, data } = error.response;
      const { common } = getSnackbarMessages();

      switch (status) {
        case 401:
          if (error.config?.skipSessionRedirect) {
            break;
          }
          expireAuthenticatedSession(common.session_expired);
          break;
        case 400:
          break;
        case 403:
          if (readRequiresOrgSelection()) {
            const requestUrl = error.config?.url ?? '';
            const isOrgSelectionRequest = requestUrl.includes('/organizations');

            if (!isOrgSelectionRequest && typeof window !== 'undefined') {
              savePostAuthRedirect(`${window.location.pathname}${window.location.search}`);
              window.location.href = '/select-organization';
            }
          }
          break;
        default:
          getStore().dispatch(
            showSnackbar({
              message: error.response.data.message || common.unexpected_error,
              severity: 'error',
            }),
          );
      }
    }
    return Promise.reject(error);
  },
);

const createStandardResponseInterceptor = () => ({
  onFulfilled: (response) => {
    const { common } = getSnackbarMessages();
    if (response.status === 202) {
      getStore().dispatch(
        showSnackbar({
          message: response.data?.message || common.request_accepted_success,
          severity: 'success',
        }),
      );
    }
    return response;
  },
  onRejected: (error) => {
    if (error?.config?.skipErrorSnackbar) {
      return Promise.reject(error);
    }

    if (error.response) {
      const { status } = error.response;
      const { common } = getSnackbarMessages();

      switch (status) {
        case 401:
          if (error.config?.skipSessionRedirect) {
            break;
          }
          expireAuthenticatedSession(common.session_expired);
          break;
        case 400:
          break;
        case 403:
          if (readRequiresOrgSelection()) {
            const requestUrl = error.config?.url ?? '';
            const isOrgSelectionRequest = requestUrl.includes('/organizations');

            if (!isOrgSelectionRequest && typeof window !== 'undefined') {
              savePostAuthRedirect(`${window.location.pathname}${window.location.search}`);
              window.location.href = '/select-organization';
            }
          }
          break;
        default:
          getStore().dispatch(
            showSnackbar({
              message: error.response.data.message || common.unexpected_error,
              severity: 'error',
            }),
          );
      }
    }
    return Promise.reject(error);
  },
});

const standardResponseInterceptor = createStandardResponseInterceptor();

authApiClient.interceptors.response.use(
  standardResponseInterceptor.onFulfilled,
  standardResponseInterceptor.onRejected,
);

billingClient.interceptors.response.use(
  standardResponseInterceptor.onFulfilled,
  standardResponseInterceptor.onRejected,
);

analyticsClient.interceptors.response.use(
  standardResponseInterceptor.onFulfilled,
  standardResponseInterceptor.onRejected,
);

export { apiClient, authApiClient, billingClient, analyticsClient, reconClient };
